import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Typography } from '@/components';
import { Route } from '@/constants';
import { useNotifications } from '@/features/notifications';

import { useInviteUserMutation } from '../../api';
import { TeamDrawer } from './drawer';
import { UserFormFields } from './form';

export type InviteMemberDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
};

/**
 * Invite a member from anywhere in the app (header, Company > Team).
 * Outside Company it also tells users where their team members are listed.
 */
export function InviteMemberDrawer({ isOpen, onClose }: InviteMemberDrawerProps) {
  const { t } = useTranslation(['team']);
  const { add } = useNotifications();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [inviteUser] = useInviteUserMutation();

  const isOnCompany = pathname.startsWith(Route.CompanySettings);

  const handleSubmit = useCallback(
    (data: UserFormFields) => {
      const dataToInviteUser = { ...data, show_modal_ttcc: true, accepted_ttcc: false };

      inviteUser(dataToInviteUser)
        .unwrap()
        .then(() => {
          add({
            id: new Date().toString(),
            title: t('Invitation sent'),
            description: isOnCompany
              ? t('Your invitation has been sent.')
              : t('Your invitation has been sent. You can see your team members in Company.'),
          });
          onClose();
        })
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There has been a data inconsistency error. Please contact the administrator.'),
          });
          onClose();
        });
    },
    [inviteUser, add, t, onClose, isOnCompany]
  );

  const goToTeam = useCallback(() => {
    onClose();
    navigate(Route.CompanySettings);
  }, [onClose, navigate]);

  return (
    <TeamDrawer isOpen={isOpen} close={onClose} onSubmit={handleSubmit}>
      {isOnCompany ? null : (
        <div className="mt-6 border-t border-neutral-50 pt-4">
          <Typography as="p" size="body-3" color="secondary">
            {t('You can see and manage your team members in Company.')}{' '}
            <button type="button" onClick={goToTeam} className="font-sans text-primary-700 font-medium hover:underline">
              {t('View your team')}
            </button>
          </Typography>
        </div>
      )}
    </TeamDrawer>
  );
}
